import type {
  Conversation,
  ConversationsQuery,
  NoticeKey,
  NoticeWordingInput,
  NoticeWordingView,
  PostMessage,
  Thread,
} from '@cyberscore/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

const key = (t: string | undefined, id: string) => ['assessments', t, id, 'messages'] as const;
const listKey = (t: string | undefined) => ['conversations', t] as const;

/** An open conversation checks for new entries this often, so it reads as live. */
const LIVE_MS = 4_000;

/**
 * Every conversation this person takes part in, newest first, with what is unread (the unread count in the
 * navigation, and the Messages list before it is filtered). Refreshes on its own.
 */
export function useConversations(enabled = true) {
  return useConversationList({}, enabled);
}

/**
 * The Messages list as filtered and sorted on the page. The defaults are left out of the address, so the
 * unfiltered list is one load shared with the navigation; while a new filter loads, the last list stays.
 */
export function useConversationList(f: ConversationsQuery, enabled = true) {
  const { tenant } = useSession();
  const params = new URLSearchParams(
    Object.entries(f).filter(([k, v]) => v && !(k === 'show' && v === 'all') && !(k === 'sort' && v === 'recent')) as [string, string][], // prettier-ignore
  ).toString();
  return useQuery({
    queryKey: [...listKey(tenant?.id), params],
    queryFn: () => api<Conversation[]>('GET', `/messages${params ? `?${params}` : ''}`),
    enabled: !!tenant && enabled,
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
  });
}

/** An assessment's thread (task 65). Polls while open, so replies show up without a reload. */
export function useThread(id: string, opts: { poll?: boolean; enabled?: boolean } = {}) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: key(tenant?.id, id),
    queryFn: () => api<Thread>('GET', `/assessments/${id}/messages`),
    enabled: !!tenant && (opts.enabled ?? true),
    refetchInterval: opts.poll ? LIVE_MS : false,
  });
}

export function usePostMessage(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (body: PostMessage) => api<Thread>('POST', `/assessments/${id}/messages`, body),
    // A load already on its way would land after the post and put the older thread back.
    onMutate: () => qc.cancelQueries({ queryKey: key(tenant?.id, id) }),
    onSuccess: (t) => {
      qc.setQueryData(key(tenant?.id, id), t);
      void qc.invalidateQueries({ queryKey: listKey(tenant?.id) });
    },
  });
}

export function useMarkRead(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: () => api('POST', `/assessments/${id}/messages/read`),
    onSuccess: () => {
      qc.setQueryData<Thread>(key(tenant?.id, id), (t) => (t ? { ...t, unread: 0 } : t));
      qc.setQueriesData<Conversation[]>({ queryKey: listKey(tenant?.id) }, (l) => l?.map((c) => (c.assessment_id === id ? { ...c, unread: 0 } : c))); // prettier-ignore
    },
  });
}

const wordingKey = (t?: string) => ['notices', t] as const;

export function useNoticeWording() {
  const { tenant } = useSession();
  return useQuery({
    queryKey: wordingKey(tenant?.id),
    queryFn: () => api<NoticeWordingView[]>('GET', '/settings/notices'),
    enabled: !!tenant,
  });
}

export function useSetWording() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: ({ key: k, body }: { key: NoticeKey; body: NoticeWordingInput }) =>
      api<NoticeWordingView[]>('PUT', `/settings/notices/${k}`, body),
    onSuccess: (list) => qc.setQueryData(wordingKey(tenant?.id), list),
  });
}

export function useResetWording() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (k: NoticeKey) => api<NoticeWordingView[]>('DELETE', `/settings/notices/${k}`),
    onSuccess: (list) => qc.setQueryData(wordingKey(tenant?.id), list),
  });
}
